/**
 * Channel/PWA Type Overrides
 *
 * This file is for overriding/correcting the swagger-generated types. When overriding a type:
 * 1. Import the incorrect type without "Dto" so that the original name can be used for export
 * 2. Add a jsdoc-style comment with the name of the original type (just for file readability)
 * 3. Create new overridden type and export it using the original name from data-contracts
 * 4. Explicitly re-export the new type from index.ts in this folder (necessary to avoid ambiguity)
 */
import {
    AccountAccessModelDto,
    AccountSummaryModelDto,
    AchBatchFilterModelDto as AchBatchFilterModel,
    AchPaymentFilterModelDto as AchPaymentFilterModel,
    AchRecipientFilterModelDto as AchRecipientFilterModel,
    CustomerModelDto,
    DownloadBaseDto,
    ErrorHttpContentDto,
    ErrorResponseModelDto,
    GetItemsResponseModelDto as GetItemsResponseWithoutCustomerModelDto,
    InstitutionModelDto as InstitutionModel,
    InternalTransferAddTransactionDetailModelDto as InternalTransferAddTransactionDetailModel,
    InternalTransferAddTransactionModelDto as InternalTransferAddTransactionModel,
    ItemModelDto as ItemWithoutCustomerModelDto,
    SearchCheckExceptionModelDto,
    SecurityMessageModelDto,
    UserModelDto,
} from './generated/data-contracts';

import {
    CheckExceptionClient as GeneratedCheckExceptionClient,
    CompanyAccountsClient as GeneratedCompanyAccountsClient,
    DownloadClient as GeneratedDownloadClient,
    InternalTransfersClient as GeneratedInternalTransfersClient,
    UsersClient as GeneratedUsersClient,
} from './generated/clients';

import { Injectable } from '@jack-henry/frontend-utils/di';
import { AddTransactionRequest } from '@treasury/domain/transfers';
import { ContentType, RequestParams } from './generated/http-client';

/**
 * AchBatchFilterModelDto
 */

type RequiredAchBatchFilterModelProps =
    | 'achCompanyList'
    | 'batchName'
    | 'creditAmountType'
    | 'debitAmountType'
    | 'secCode'
    | 'status'
    | 'taxTemplateType'
    | 'updatedDateType';

export type AchBatchFilterModelDto = Pick<AchBatchFilterModel, RequiredAchBatchFilterModelProps> &
    Partial<Exclude<AchBatchFilterModel, RequiredAchBatchFilterModelProps>>;

/**
 * AchPaymentFilterModelDto
 */

type RequiredAchPaymentFilterModelProps =
    | 'achCompanyList'
    | 'batchName'
    | 'creditAmountType'
    | 'debitAmountType'
    | 'secCode'
    | 'status';

export type AchPaymentFilterModelDto = Pick<
    AchPaymentFilterModel,
    RequiredAchPaymentFilterModelProps
> &
    Partial<Exclude<AchPaymentFilterModel, RequiredAchPaymentFilterModelProps>>;

/**
 * AchRecipientFilterModelDto
 */
type RequiredAchRecipientFilterModelProps = 'recordType' | 'recipientName';

export type AchRecipientFilterModelDto = Pick<
    AchRecipientFilterModel,
    RequiredAchRecipientFilterModelProps
> &
    Partial<Exclude<AchRecipientFilterModel, RequiredAchRecipientFilterModelProps>>;

/**
 * ItemModelDto
 */

export interface ItemModelDto extends ItemWithoutCustomerModelDto {
    customer?: CustomerModelDto;
    frontImage: string;
    rearImage: string;
}

/**
 * GetItemsResponseModelDto
 */

export type GetItemsResponseModelDto = GetItemsResponseWithoutCustomerModelDto & {
    items: ItemModelDto[];
};

/**
 * InstitutionModelDto
 */

export enum PaymentDateModelDto {
    None = 0,
    ProcessDate = 1,
    DueDate = 2,
}

/**
 * InstitutionModelDto
 */

export interface InstitutionModelDto extends Omit<InstitutionModel, 'paymentDateModel'> {
    paymentDateModel: PaymentDateModelDto;
}

export type SaveUserAccountAccessModelDto = {
    addedAccounts?: AccountAccessModelDto[];
    removedAccounts?: AccountAccessModelDto[];
    securityMessage?: SecurityMessageModelDto;
};

/**
 * InternalTransferAddTransactionDetailModelDto,
 */

// paymentOptionType is listed as required in the generated code, but it is optional in the API
export interface InternalTransferAddTransactionDetailModelDto
    extends Omit<InternalTransferAddTransactionDetailModel, 'paymentOptionType'> {
    paymentOptionType?: InternalTransferAddTransactionDetailModel['paymentOptionType'];
}

@Injectable()
export class DownloadClient extends GeneratedDownloadClient {
    downloadPost = (model: DownloadBaseDto | null, params: RequestParams = {}) =>
        // @ts-expect-error
        this.http.request<File, any>({
            path: `/download`,
            method: 'POST',
            body: model,
            ...params,
        });
}

@Injectable()
export class CheckExceptionClient extends GeneratedCheckExceptionClient {
    checkExceptionDownloadPdfCheckExceptions = (
        searchCheckExceptionModel: SearchCheckExceptionModelDto | null,
        params: RequestParams = {}
    ) =>
        // @ts-expect-error
        this.http.request<string, ErrorHttpContentDto>({
            path: `/CheckException/Download/Pdf`,
            method: 'POST',
            body: searchCheckExceptionModel,
            ...params,
        });

    checkExceptionDownloadCsvCheckExceptions = (
        searchCheckExceptionModel: SearchCheckExceptionModelDto | null,
        params: RequestParams = {}
    ) =>
        // @ts-expect-error
        this.http.request<string, ErrorHttpContentDto>({
            path: `/CheckException/Download/Csv`,
            method: 'POST',
            body: searchCheckExceptionModel,
            ...params,
        });
}

/**
 * InternalTransfersClient
 */

@Injectable()
export class InternalTransfersClient extends GeneratedInternalTransfersClient {
    // @ts-expect-error
    internalTransfersPost = (
        query: {
            type: string | null;
        },
        internalTransfer: AddTransactionRequest,
        params: RequestParams = {}
    ) =>
        // @ts-expect-error
        this.http.request<InternalTransferAddTransactionModel, ErrorResponseModelDto>({
            path: `/InternalTransfers`,
            method: 'POST',
            query: query,
            body: internalTransfer,
            format: 'json',
            ...params,
        });

    // @ts-expect-error
    internalTransfersSaveTransferTemplate = (
        query: {
            type: string | null;
        },
        transferTemplate: AddTransactionRequest,
        params: RequestParams = {}
    ) =>
        // @ts-expect-error
        this.http.request<InternalTransferAddTransactionModel, ErrorResponseModelDto>({
            path: `/internalTransfers/saveTransferTemplate`,
            method: 'POST',
            query: query,
            body: transferTemplate,
            format: 'json',
            ...params,
        });

    internalTransfersUpdateTransferTemplate = (
        transferTemplate: AddTransactionRequest | null,
        params: RequestParams = {}
    ) =>
        // @ts-expect-error
        this.http.request<InternalTransferAddTransactionModel, ErrorResponseModelDto>({
            path: `/internalTransfers/updateTransferTemplate`,
            method: 'PUT',
            body: transferTemplate,
            type: ContentType.Json,
            format: 'json',
            ...params,
        });
}

/* UserStateModelDto */
export interface UserStateModelDto {
    /** @format int32 */
    userId: number;
    name?: string;
    isActive: boolean;
    isLocked: boolean;
    approvalRequired: boolean;
    pendingState?: string;
    editedByUser?: UserModelDto;
    /** @format date-time */
    editedDate?: string;
    hasPendingChanges: boolean;
    isEnrolled: boolean;
    isEditor: boolean;
    hasMinimumRequirements: boolean;
    isSuperUser: boolean;
    alias?: string;
}

@Injectable()
export class UsersClient extends GeneratedUsersClient {
    /**
     * Override because the generated method expects the wrong response type (UserSummaryModelDto vs UserStateModelDto (defined above))
     */
    usersGetState = (id: number, params: RequestParams = {}) =>
        // @ts-expect-error
        this.http.request<UserStateModelDto, any>({
            path: `/users/${id}/header`,
            method: 'GET',
            format: 'json',
            ...params,
        });

    usersDeleteUser = (id: number, params: RequestParams = {}) => {
        // @ts-expect-error
        this.http.request<boolean, any>({
            path: `/users/${id}`,
            method: 'DELETE',
            format: 'json',
            ...params,
        });
    };
}

@Injectable()
export class CompanyAccountsClient extends GeneratedCompanyAccountsClient {
    /* Generated client does not include PUT to update account nicknames */
    companyAccountUpdate = (
        id: number,
        accountSummary: AccountSummaryModelDto,
        params: RequestParams = {}
    ) =>
        // @ts-expect-error
        this.http.request<AccountSummaryModelDto, any>({
            path: `/companyaccounts//${id}`,
            body: accountSummary,
            method: 'PUT',
            format: 'json',
            ...params,
        });

    companyAccountNickNamesUpdate = (
        accountSummaries: AccountSummaryModelDto[],
        params: RequestParams = {}
    ) =>
        // @ts-expect-error
        this.http.request<AccountSummaryModelDto[], any>({
            path: `/companyaccounts/accountNickNames`,
            body: accountSummaries,
            method: 'PUT',
            format: 'json',
            ...params,
        });
}
